/**
 * Single Source of Truth (SSOT) for tenant modules.
 *
 * This registry is the ONLY place where the catalog of modules is defined.
 * Backend (`packages/backend`), tenant frontend (`packages/frontend`) and
 * master frontend (`packages/frontend-master`) all import from here.
 *
 * Adding or removing a module requires a single edit to this file.
 *
 * IMPORTANT: `key` values must NEVER be changed once shipped — they are stored
 * verbatim in `tenant_modules.module_key` (DB) and Redis cache keys.
 *
 * Wave 2 (Task #531):
 *   - `landing_pages` + `site_builder` unified into `web_builder`.
 *   - POS sub-modules (`pos_cassa`, `pos_catalogo`, `pos_amministrazione`)
 *     promoted to first-class registry entries with explicit `parent`
 *     relationship to `pos_warehouse`.
 *
 * Wave 3 (Task #532):
 *   - Added `category` and `order` presentation metadata so the master UI can
 *     group modules into collapsible sections. Backend ignores these fields.
 */

/**
 * Categories used to group modules in the master UI (Wave 3).
 * `order` controls the display order of category sections.
 *
 * IMPORTANT: This metadata is presentation-only — the backend ignores it.
 */
export const MODULE_CATEGORIES = [
  { key: 'communication', label: 'Comunicazione & Telefonia', order: 1 },
  { key: 'crm_marketing', label: 'CRM & Marketing', order: 2 },
  { key: 'web', label: 'Web', order: 3 },
  { key: 'pos_warehouse', label: 'POS & Magazzino', order: 4 },
  { key: 'ai', label: 'AI', order: 5 },
  { key: 'productivity', label: 'Produttività', order: 6 },
  { key: 'hr', label: 'HR & Collaborazione', order: 7 },
] as const;

export type ModuleCategoryKey = typeof MODULE_CATEGORIES[number]['key'];

/**
 * Registry declared with `as const` so each `key` keeps its string-literal
 * type. Do NOT add an explicit `ReadonlyArray<ModuleDefinition>` annotation
 * here — it would widen `key` to `string` and defeat the literal union.
 *
 * `category` and `order` are presentation-only metadata used by the master UI
 * to render hierarchical, grouped controls (Wave 3). They are ignored by the
 * backend toggle endpoint.
 *
 * Entries with a `parent` field are sub-modules: they share the parent's
 * category and the master UI renders them indented under it.
 */
export const MODULE_REGISTRY = [
  { key: 'pbx', name: 'PBX / Telefonia', description: 'Gestione interni, code, IVR, chiamate, CDR, registrazioni, switchboard, provisioning telefoni (BLF, phonebook, configurazione device)', category: 'communication', order: 1 },
  { key: 'email', name: 'Email', description: 'Gmail integration, account SMTP/IMAP, template email', category: 'communication', order: 2 },
  { key: 'messenger', name: 'Messenger', description: 'Chat interna, gruppi di lavoro, canali broadcast, presenza online', category: 'communication', order: 3 },
  { key: 'crm', name: 'CRM', description: 'Contatti, lead, aziende, pipeline Kanban, form embedding', category: 'crm_marketing', order: 1 },
  { key: 'campaigns', name: 'Campagne & Automazioni', description: 'Campagne SMS/Email/WhatsApp, Workflow Builder, trigger automatici', category: 'crm_marketing', order: 2 },
  { key: 'web_builder', name: 'Web Builder', description: 'Creazione siti web e landing page, template, AI generation, editor visuale drag & drop', category: 'web', order: 1 },
  { key: 'pos_warehouse', name: 'Cassa e Magazzino', description: 'POS, magazzino, inventario, lotti, listini prezzi, vendite, credito clienti', category: 'pos_warehouse', order: 1 },
  { key: 'pos_cassa', name: 'POS — Cassa', description: 'Punto cassa, vendite e impostazioni cassa', category: 'pos_warehouse', order: 2, parent: 'pos_warehouse' },
  { key: 'pos_catalogo', name: 'POS — Catalogo', description: 'Catalogo prodotti, listini, lotti, fornitori', category: 'pos_warehouse', order: 3, parent: 'pos_warehouse' },
  { key: 'pos_amministrazione', name: 'POS — Amministrazione', description: 'Amministrazione vendite, credito clienti, reportistica', category: 'pos_warehouse', order: 4, parent: 'pos_warehouse' },
  // Task #3405 — modulo Incentivazione (commissioning/gare): default SPENTO,
  // attivazione per-tenant via tenant_modules dal master. SoT analisi:
  // docs/incentivazione-analisi.md.
  { key: 'incentivazione', name: 'Incentivazione', description: 'Commissioning: gare insegna/sell-in/risorse, configuratori, value packages, variabili, cluster, add-on commerciali', category: 'pos_warehouse', order: 5 },
  { key: 'ai_tools', name: 'AI Tools', description: 'RAG, Knowledge Base, Chatbot, Voice Agent, Personal Assistant', category: 'ai', order: 1 },
  { key: 'company_wiki', name: 'Company Wiki', description: 'Knowledge base aziendale curata da agente AI: ingest documenti, pagine markdown interlinkate, scheduler con budget token, cost cap & tracking, MCP tool interni ed esterni', category: 'ai', order: 2 },
  { key: 'calendar', name: 'Calendario', description: 'Agenda appuntamenti, gestione consulenti e disponibilità, prenotazioni online via form pubblici, promemoria automatici, integrazione con CRM, HR e centralino', category: 'productivity', order: 1 },
  { key: 'hr', name: 'HR', description: 'Gestione turni, timbrature, presenze, richieste ferie/permessi, gerarchie dipendenti', category: 'hr', order: 1 },
] as const;

export type ModuleKey = typeof MODULE_REGISTRY[number]['key'];

export interface ModuleDefinition {
  key: ModuleKey;
  name: string;
  description: string;
  category: ModuleCategoryKey;
  order: number;
  parent?: ModuleKey;
}

/**
 * Backwards-compat: list of POS sub-module keys (now living in the main
 * registry as children of `pos_warehouse`). Some legacy code still imports
 * `SUBMODULE_KEYS`; we keep it derived from the registry so it stays in sync.
 */
export const SUBMODULE_KEYS = MODULE_REGISTRY
  .filter((m): m is typeof m & { parent: ModuleKey } => 'parent' in m && !!(m as { parent?: ModuleKey }).parent)
  .map((m) => m.key) as ReadonlyArray<ModuleKey>;

export type SubmoduleKey = typeof SUBMODULE_KEYS[number];

/** Convenience: list of every module key (top-level + sub-modules), literal-typed. */
export const MODULE_KEYS = MODULE_REGISTRY.map((m) => m.key) as ReadonlyArray<ModuleKey>;

/** Alias kept for code that still imports the older name. Same content as MODULE_KEYS. */
export const ALL_MODULE_KEYS: ReadonlyArray<ModuleKey> = MODULE_KEYS;

/**
 * Legacy module keys that have been removed from the registry.
 * Kept here so we can clean up DB rows / cache entries during migration.
 *  - `provisioning` was folded into `pbx` (Wave 1).
 *  - `landing_pages` + `site_builder` were unified into `web_builder` (Wave 2).
 */
export const DEPRECATED_MODULE_KEYS = ['provisioning', 'landing_pages', 'site_builder'] as const;

export type DeprecatedModuleKey = typeof DEPRECATED_MODULE_KEYS[number];

/**
 * Mapping from legacy/deprecated keys to their replacement.
 * Used by the backend guard (so old callers keep working) and to power the
 * one-time DB migration that promotes legacy rows to the new key.
 */
export const LEGACY_MODULE_ALIASES: Readonly<Record<string, ModuleKey>> = {
  landing_pages: 'web_builder',
  site_builder: 'web_builder',
};

export function getModuleDefinition(key: string): ModuleDefinition | undefined {
  return MODULE_REGISTRY.find((m) => m.key === key) as ModuleDefinition | undefined;
}

export function getChildModules(parentKey: string): ModuleDefinition[] {
  return MODULE_REGISTRY.filter((m) => (m as { parent?: string }).parent === parentKey) as unknown as ModuleDefinition[];
}

export function getParentModule(key: string): ModuleKey | undefined {
  return getModuleDefinition(key)?.parent;
}

export function resolveModuleKey(key: string): string {
  return LEGACY_MODULE_ALIASES[key] || key;
}
